import axios, {AxiosInstance} from "axios";
import AppConfig from "@/config/appConfig";
import {itExist} from "@/utils/general-utils";
import AuthService from "@/api/authService";
import router from "@/router";

interface IndicatorItem {
  value: string | null;
  label: string;
  default?: boolean;
}

interface IndicatorListResponse {
  list: IndicatorItem[];
  lang: string;
}

export interface ErrorInterface {
  error: true;
  publicResponse: PublicResponseInterface;
}

export interface PublicResponseInterface {
  messages: PublicResponseMessagesInterface;
  invalidEntries: PublicResponseInvalidEntriesInterface[];
}

interface PublicResponseMessagesInterface {
  [key: string]: string;
}

interface PublicResponseInvalidEntriesInterface {
  field: string;
  key: string;
  message: string;
}

export interface GetUserResponse {
  histories: UserHistoryApi[];
  products: ProductApi[];
  user: UserInformationsApi;
}

export interface UserHistoryApi {
  author: string;
  content: { message: string };
  creationDate: string;
  id: number;
  type: 'manual' | 'automatic';
  serial?: SerialApi;
  notificationId: number | null;
}

export interface GetUsersResponse {
  users: UserInformationsApi[];
  totalItems: number;
}

interface UserInformationsApi {
  active: boolean;
  civility: string;
  creationDate: string;
  credential: Credential;
  email?: string;
  firstname: string;
  lastname: string;
  gigyaId?: string;
  id: string;
  langCode: string;
  lastActivityDate: string;
  lastRegisterDate: string;
  mobile?: string;
  nbrOfConnections: number;
  phoneDetails: PhoneDetails;
  serials: SerialApi[];
  source: string;
  status: UserStatusEnum;
  consumer: UserConsumer;
}

export interface UserConsumer {
  id: string;
  name: string;
  ocp: string;
  isActiveNotification: boolean;
}

export enum SerialStatusEnum {
  OK = 'serial-ok',
  WAITING_FOR_INFORMATION = 'serial-waiting-for-information',
  SOLD = 'serial-sold',
  NOT_SOLD = 'serial-not-sold',
}

export enum UserStatusEnum {
  OK = 'user-ok',
  SUSPICIOUS = 'user-suspicious',
}

export enum UserConsumerEnum {
  PRODUCT_PAGE_WEB_APP = 'product-page-webapp',
  FASHION_APP_US = 'fashion-app-us',
  MY_ACCOUNT_APP = 'my-account-app',
  MY_BOUTIQUE_APP = 'my-boutique-app',
}

export const serialStatusPriorityOrder = [
  SerialStatusEnum.OK,
  SerialStatusEnum.SOLD,
  SerialStatusEnum.WAITING_FOR_INFORMATION,
  SerialStatusEnum.NOT_SOLD,
];

export const tableVisibleStatus = [SerialStatusEnum.WAITING_FOR_INFORMATION, SerialStatusEnum.SOLD, SerialStatusEnum.NOT_SOLD, UserStatusEnum.SUSPICIOUS];
export const tableDeletableStatus = [SerialStatusEnum.NOT_SOLD];

export const tableStatusText = new Map<SerialStatusEnum | UserStatusEnum, string>([
  [SerialStatusEnum.WAITING_FOR_INFORMATION, 'Waiting for informations'],
  [SerialStatusEnum.SOLD, 'Sold'],
  [SerialStatusEnum.NOT_SOLD, 'Unsold product'],
  [SerialStatusEnum.OK, 'OK'],
  [UserStatusEnum.OK, 'OK'],
  [UserStatusEnum.SUSPICIOUS, 'Suspicious account'],
]);

export const tableConsumerText = new Map<UserConsumerEnum, string>([
  [UserConsumerEnum.PRODUCT_PAGE_WEB_APP, 'Digital Passport'],
  [UserConsumerEnum.FASHION_APP_US, 'Fashion App US'],
  [UserConsumerEnum.MY_ACCOUNT_APP, 'My Account'],
  [UserConsumerEnum.MY_BOUTIQUE_APP, 'My Boutique App'],
]);

interface Credential {
  type: 'email' | 'mobile';
  value: string;
}

interface PhoneDetails {
  countryCode: number;
  countryCodeSource: number;
  format: string;
  link: string;
  nationalNumber: string;
  numberType: number;
  regionCode: string;
}

export interface SerialApi {
  id: string;
  serialNumber: string;
  countryCode?: string;
  origin?: string;
  status: SerialStatusEnum;
  deletedAt?: Date;
}

export interface Admin {
  id: number;
  account: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  type: number;
  active?: boolean;
  countries: string[];
  creationDate: string;
  lastConnection: string;
  superAdmin: boolean;
  deletable: boolean;
  status: AdminStatuses;
}

export interface GetAdminsResponse {
  admins: Admin[];
  totalItems: number;
}

export interface ProductApi {
  boutique: Boutique;
  color: string;
  fabric: string;
  name: string;
  photoUrl: string | null;
  productCode: string;
  reference: string;
  retailCountryName: string;
  retailDate: string | null;
  retailLocationName: string;
  serialNumber: string;
  status: SerialStatusEnum;
  warrantyActive: boolean;
  warrantyEligible: boolean;
  warrantyEndDate: string;
  deletedAt: string | null;
}

interface Boutique {
  address: string;
  addressComplement: string;
  city: string;
  countryCode: string;
  name: string;
  phone: string;
  postalCode: string;
}

interface FooterResponse {
  fromCache: boolean;
  html: string;
  type: 'one' | 'legacy';
  url: string;
}

interface CsvResponse {
  csv: string;
  filename: string;
}

export enum AdminRoles {
  LOCAL_ADMIN = 10,
  PRODUCT_PAGE_ADMIN = 80,
  SUPER_ADMIN = 99,
}

export enum AdminStatuses {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
  INVITED = 'invited',
}

export interface CreateAdminInterface {
  account: string;
  email: string;
  firstName: string;
  lastName: string;
  type: number;
  countries?: string[];
}

export interface EditAdminInterface {
  email: string;
  firstName: string;
  lastName: string;
  type: number;
  countries?: string[];
}

export default class ApiService {

  public static api: AxiosInstance;

  public static init(): void {
    const headers: any = {
      'IDC-Source': 'BO',
    };
    if (itExist(AuthService.getToken())) {
      headers['Authorization'] = 'Bearer ' + AuthService.getToken();
    }
    ApiService.api = axios.create({
      baseURL: AppConfig.API_URL,
      withCredentials: true,
      headers,
    });

    ApiService.api.interceptors.response.use((response) => {
        return Promise.resolve(response);
      }, (error) => {

        if (error?.response?.status === 401) {
          AuthService.removeAuthStorage();
          router.push({name: 'login'});
        }

        return Promise.reject(error);
      },
    );
  }

  public static async removeAllAccess(userId: string): Promise<any> {
    return ApiService.api.post('/admin/remove-access', {
      userId,
    }).then(r => r.data);
  }

  public static async anonymise(userId: string): Promise<any> {
    return ApiService.api.post('/admin/anonymise', {
      userId,
    }).then(r => r.data);
  }

  public static async sendSerialLink(serialId: string): Promise<any> {
    return ApiService.api.post('/admin/send-serial-link', {
      serialId,
    }).then(r => r.data);
  }

  public static async dissociateSerial(serialId: string, userId: string): Promise<any> {
    return ApiService.api.post('/admin/dissociate-serial', {
      serialId,
      userId,
    }).then(r => r.data);
  }

  public static async removeTagSerial(serialId: string): Promise<any> {
    return ApiService.api.post('/admin/remove-status-serial', {
      serialId,
    }).then(r => r.data);
  }

  public static async updateCredentials(userId: string, email?: string, mobile?: string, firstname?: string, lastname?: string): Promise<any> {
    return ApiService.api.post('/admin/update-credentials', {
      userId,
      email,
      mobile,
      firstname,
      lastname,
    }).then(r => r.data);
  }

  public static getIndicatorList(): Promise<IndicatorListResponse> {
    return ApiService.api.get('/admin/mobile-indicator-list').then((r) => r.data);
  }

  public static getPublicResponseAsText(data: ErrorInterface) {
    if (data.error) {
      const messages = data.publicResponse.messages;
      const invalidEntries = data.publicResponse.invalidEntries;
      const messageArray = [];

      for (const messageKey in messages) {
        if (Object.prototype.hasOwnProperty.call(messages, messageKey)) {
          messageArray.push(messages[messageKey]);
        }
      }
      for (const invalidEntry of invalidEntries) {
        messageArray.push(invalidEntry.message);
      }

      return messageArray.join(', ');
    }

    return 'Unknown error';
  }

  public static getUser(userId: string, sortProductsByStatusAndRegisterDate = false): Promise<GetUserResponse> {
    return ApiService.api.post('/admin/user', {
      userId,
      sortProductsByStatusAndRegisterDate: sortProductsByStatusAndRegisterDate
    }).then(r => r.data);
  }

  public static getUsers(parametersArray: any[]): Promise<GetUsersResponse> {
    const parameters = parametersArray.length > 0 ? parametersArray.reduce((finalText = "", value) => finalText += value) : "";
    return ApiService.api.get('/admin/users' + parameters).then(r => r.data);
  }

  public static getAdmins(parametersArray: any[]): Promise<GetAdminsResponse> {
    const parameters = parametersArray.length > 0 ? parametersArray.reduce((finalText = "", value) => finalText += value) : "";
    return ApiService.api.get('/admin/administrators' + parameters).then(r => r.data);
  }

  public static postCreateAdmin(admin: CreateAdminInterface): Promise<Record<string, unknown>> {
    return ApiService.api.post('/admin/administrators', admin, {
      headers: {
        'Content-Type': 'application/merge-patch+json',
      }
    }).then(r => r.data);
  }

  public static patchEditAdmin(id: number, admin: EditAdminInterface): Promise<Record<string, unknown>> {
    return ApiService.api.patch(`/admin/administrators/${id}`, admin, {
      headers: {
        'Content-Type': 'application/merge-patch+json',
      }
    }).then(r => r.data);
  }

  public static toggleActiveStatusAdmin(id: Admin["id"], enable: boolean): Promise<Admin> {
    return ApiService.api.patch(`/admin/administrators/${id}`, { active: enable}, {
      headers: {
        'Content-Type': 'application/merge-patch+json',
      }
    }).then(r => r.data);
  }

  public static deleteAdmin(admin: Admin): Promise<Record<string, unknown>> {
    return ApiService.api.delete(`/admin/administrators/${admin.id}`).then(r => r.data);
  }

  public static getEventLogs(parametersArray: any[]): Promise<CsvResponse> {
    const parameters = parametersArray.length > 0 ? parametersArray.reduce((finalText = "", value) => finalText += value) : "";
    return ApiService.api.get('/admin/event-logs' + parameters).then(r => r.data);
  }

  public static getRegisterLogs(parametersArray: any[]): Promise<CsvResponse> {
    const parameters = parametersArray.length > 0 ? parametersArray.reduce((finalText = "", value) => finalText += value) : "";
    return ApiService.api.get('/admin/register-logs' + parameters).then(r => r.data);
  }

  public static getFooter(): Promise<FooterResponse> {
    return ApiService.api.get('/admin/footer').then(r => r.data);
  }

  public static addComment(userId: string, comment: string) {
    return ApiService.api.post('/admin/add-comment', {
      userId,
      comment,
    }).then(r => r.data);
  }

  public static startUpdateTranslations(forceClearCache: boolean) {
    return ApiService.api.post('/aem/start-update-translations', {forceClearCache}).then(r => r.data);
  }
}
